import { ChevronDownIcon, ChevronRightIcon, ExternalLinkIcon, LoaderCircleIcon } from "lucide-react";
import { memo, useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { CallSpan, TurnStep } from "../../../../shared/api.ts";
import { Code } from "@/components/common/code.tsx";
import { MarkdownText } from "@/components/common/markdown.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { formatCodesSafely } from "@/lib/codes.ts";
import { groupSteps, stepDurationMs, type StepGroup } from "@/lib/turns.ts";
import { cn, prettyJson } from "@/lib/utils.ts";
import { stepIcon } from "./step-icon.tsx";

function hasDetail(step: TurnStep): boolean {
  const detail = step.detail;
  if (!detail) return false;
  return Boolean(
    detail.text ||
      detail.query ||
      detail.url ||
      detail.tool ||
      detail.arguments !== undefined ||
      detail.result ||
      detail.error ||
      (detail.codes && detail.codes.length > 0),
  );
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-semibold tracking-[0.04em] text-muted-foreground uppercase">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function StepDetail({ step }: { step: TurnStep }) {
  const { t } = useI18n();
  const detail = step.detail;
  if (!detail) return null;
  const textIsReasoning = step.kind === "reasoning";
  return (
    <div className="mt-1 mb-2 ml-[30px] grid gap-3 rounded-lg border bg-muted/50 p-3 text-[12.5px]">
      {detail.text ? (
        textIsReasoning || step.kind === "note" || step.kind === "warning" ? (
          <MarkdownText text={detail.text} size="sm" className="text-foreground/90" />
        ) : (
          <DetailField label={t.turn.detail.text}>
            <MarkdownText text={detail.text} size="sm" />
          </DetailField>
        )
      ) : null}
      {detail.query ? (
        <DetailField label={t.turn.detail.query}>
          <span className="code-mono rounded border bg-card px-1.5 py-0.5 text-[12px] break-words">{detail.query}</span>
        </DetailField>
      ) : null}
      {detail.url ? (
        <DetailField label={t.turn.detail.url}>
          <a
            href={detail.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-start gap-1 text-primary hover:underline"
          >
            <span className="break-all">{detail.url}</span>
            <ExternalLinkIcon className="mt-0.5 size-3 shrink-0" aria-hidden />
          </a>
        </DetailField>
      ) : null}
      {detail.tool ? (
        <DetailField label={t.turn.detail.tool}>
          <code className="code-mono text-[12px]">{detail.tool}</code>
        </DetailField>
      ) : null}
      {detail.arguments !== undefined ? (
        <DetailField label={t.turn.detail.arguments}>
          <pre className="code-mono max-h-48 overflow-auto rounded-md border bg-card p-2 text-[11.5px] leading-relaxed whitespace-pre-wrap">
            {prettyJson(detail.arguments)}
          </pre>
        </DetailField>
      ) : null}
      {detail.codes && detail.codes.length > 0 ? (
        <DetailField label={t.turn.detail.codes}>
          <div className="flex flex-wrap gap-1.5">
            {detail.codes.map((code) => (
              <span key={code} className="rounded-md border bg-card px-1.5 py-0.5 text-[12px]">
                <Code value={code} />
              </span>
            ))}
          </div>
        </DetailField>
      ) : null}
      {detail.result ? (
        <DetailField label={t.turn.detail.result}>
          <pre className="code-mono max-h-56 overflow-auto rounded-md border bg-card p-2 text-[11.5px] leading-relaxed whitespace-pre-wrap">
            {detail.result}
          </pre>
        </DetailField>
      ) : null}
      {detail.error ? (
        <DetailField label={t.turn.detail.error}>
          <p className="rounded-md border border-danger-border bg-danger-soft px-2 py-1.5 text-danger">{detail.error}</p>
        </DetailField>
      ) : null}
    </div>
  );
}

const StepRow = memo(function StepRow({ step }: { step: TurnStep }) {
  const { t, fmt } = useI18n();
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const Icon = stepIcon(step);
  const expandable = hasDetail(step);
  const duration = stepDurationMs(step);
  const failed = step.status === "failed";
  const running = step.status === "running";
  const warning = step.kind === "warning";
  const label = formatCodesSafely(step.label);

  const content = (
    <>
      <span
        className={cn(
          "relative z-10 flex size-5 items-center justify-center rounded-full border bg-card",
          running && "border-primary/50 text-primary",
          failed && "border-danger-border bg-danger-soft text-danger",
          warning && !failed && "border-warning-border bg-warning-soft text-warning",
          !running && !failed && !warning && "text-muted-foreground",
        )}
      >
        <Icon className="size-3" aria-hidden />
      </span>
      <span
        className={cn(
          "min-w-0 truncate text-[13px] leading-5",
          failed ? "text-danger" : running ? "font-medium text-foreground" : "text-foreground/85",
        )}
        title={label}
      >
        <span className="sr-only">{t.turn.kinds[step.kind]}: </span>
        {label}
      </span>
      <span className="flex items-center gap-1 justify-self-end">
        {running ? (
          <LoaderCircleIcon className="size-3.5 animate-spin text-primary" aria-label={t.turn.stepRunning} />
        ) : duration !== null ? (
          <span className={cn("code-mono text-[11.5px]", failed ? "text-danger" : "text-muted-foreground")}>
            {fmt.stepDuration(duration)}
          </span>
        ) : null}
        {expandable ? (
          <ChevronRightIcon
            className={cn("size-3.5 text-muted-foreground/70 transition-transform duration-150", open && "rotate-90")}
            aria-hidden
          />
        ) : (
          <span className="size-3.5" aria-hidden />
        )}
      </span>
    </>
  );

  const rowClass =
    "grid w-full grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-md px-1.5 py-[5px] text-left";

  return (
    <li className="relative">
      {expandable ? (
        <button
          type="button"
          className={cn(rowClass, "transition-colors hover:bg-accent/70 focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none")}
          aria-expanded={open}
          aria-controls={detailId}
          onClick={() => setOpen((value) => !value)}
        >
          {content}
        </button>
      ) : (
        <div className={rowClass}>{content}</div>
      )}
      {expandable && open ? (
        <div id={detailId} className="animate-rise">
          <StepDetail step={step} />
        </div>
      ) : null}
    </li>
  );
});

function GroupBlock({ group, live }: { group: StepGroup; live: boolean }) {
  const { t, fmt } = useI18n();
  const durationMs = Math.max(0, group.endMs - group.startMs);
  const showSpinner = live && group.running;
  return (
    <section aria-label={t.phases.raw[group.phase]}>
      <div className="flex items-center gap-2 px-1.5 pt-2.5 pb-1">
        <span
          className={cn(
            "text-[11px] font-semibold tracking-[0.06em] uppercase",
            showSpinner ? "text-primary" : "text-muted-foreground",
          )}
        >
          {t.phases.raw[group.phase]}
        </span>
        <span className="h-px flex-1 bg-border" aria-hidden />
        <span className="code-mono text-[11px] text-muted-foreground">
          {t.turn.stepsCount(group.steps.length)} · {fmt.duration(durationMs)}
        </span>
      </div>
      <ol className="relative before:absolute before:top-2 before:bottom-2 before:left-[15.5px] before:w-px before:bg-border">
        {group.steps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </ol>
    </section>
  );
}

/**
 * "Process": every step of a turn grouped by phase, with durations and expandable details.
 * Open while the turn runs, collapsed to "27 steps · 3m 12s" once it is done.
 * Used by the turn card and by the admin trace page.
 */
export function StepTimeline({
  steps,
  calls,
  running,
  totalMs,
  now,
  defaultOpen,
  className,
}: {
  steps: TurnStep[];
  /** The turn's model calls, which a group's duration includes. */
  calls: CallSpan[];
  running: boolean;
  totalMs: number | null;
  now: number;
  defaultOpen?: boolean;
  className?: string;
}) {
  const { t, fmt } = useI18n();
  const [open, setOpen] = useState(defaultOpen ?? running);
  const wasRunning = useRef(running);

  useEffect(() => {
    if (wasRunning.current !== running) {
      setOpen(running);
      wasRunning.current = running;
    }
  }, [running]);

  const groups = groupSteps(steps, { running, now, calls });
  const summary = [t.turn.stepsCount(steps.length), totalMs !== null ? fmt.duration(totalMs) : null].filter(Boolean).join(" · ");

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-md py-1.5 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35">
        <ChevronDownIcon
          className="size-4 shrink-0 -rotate-90 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-0"
          aria-hidden
        />
        <span className="text-[13px] font-semibold text-foreground">{t.turn.process}</span>
        <span className="code-mono truncate text-[12px] text-muted-foreground">{summary}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pb-1">
          {groups.length === 0 ? (
            <p className="flex items-center gap-2 px-1.5 py-2 text-[13px] text-muted-foreground">
              {running ? <LoaderCircleIcon className="size-3.5 animate-spin text-primary" aria-hidden /> : null}
              {running ? t.turn.starting : t.turn.noSteps}
            </p>
          ) : (
            groups.map((group) => <GroupBlock key={group.key} group={group} live={running} />)
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
